import { compressImage } from '@src/pages/Inventory/photoUtils';
import { useMutation } from '@src/query';

import { partsKeyPrefix } from './usePartFormMutations';


/**
 * Part-detail-drawer mutation hooks (T10). Each wraps the shared `useMutation`
 * engine with the injected-`queryClient` `mutateFn({ apiClient, queryClient })`
 * contract — the corelib query singleton is NOT imported here (the drawer
 * historically read `@src/corelib/query` to invalidate the parts list; that
 * singleton import is gone). Invalidation runs inside `mutateFn`, the one place
 * `queryClient` is injected, so the exact parts cache key prefix
 * `['root','','inventory','parts',sellerId]` (and the per-part fitment
 * suggestions key) is preserved byte-for-byte against the original inline
 * handlers.
 *
 * Mirrors `query/garage/useDonorMutations.js` /
 * `query/inventory/usePartFormMutations.js`: one
 * `query/<area>/use<Thing>Mutations.js` module re-exported through the
 * `@src/query` barrel. The drawer threads its `partsKeyPrefix` (built by
 * `Inventory.jsx`) into every hook so the invalidated key matches the list
 * exactly; `partsKeyPrefix(sellerId)` is re-exported for callers that build the
 * key from a seller id (the Fitment decide path).
 */

/**
 * Invalidate the parts list as an injected-`queryClient` mutation (T10).
 * Variables: `{ keyPrefix }`. The drawer's `refetchDetail` calls this after a
 * tab mutation so list-visible fields (thumbnail, fitment, channels) refresh —
 * replacing the `@src/corelib/query` singleton the drawer previously imported to
 * invalidate `partsKeyPrefix`. Invalidation runs in the one place `queryClient`
 * is injected, matching the convention.
 */
function usePartListInvalidateMutation(params) {
  return useMutation({
    mutateFn: ({ queryClient, keyPrefix }) => {
      queryClient.invalidateQueries({ queryKey: keyPrefix });
      return Promise.resolve();
    },
    ...params,
  });
}


/**
 * Upload one or more saved photos to a part (Details tab). Variables:
 * `{ sellerId, partId, files }`. Compresses + multipart-POSTs each file
 * sequentially (index 0 lands first → backend auto-marks it primary), matching
 * the original inline upload. No invalidation here — the caller's `onSuccess`
 * (`refetchDetail`) refetches the detail aggregate AND invalidates the parts
 * list, so the table thumbnail stays in sync.
 */
function usePartPhotoUploadMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, sellerId, partId, files }) => {
      for (const file of files) {
        const compressed = await compressImage(file);
        const body = new FormData();
        body.append('image', compressed);
        await apiClient.request({
          url: `/inventory/parts/${sellerId}/${partId}/photos/`,
          method: 'POST',
          payload: body,
          contentType: 'multipart/form-data',
        });
      }
    },
    ...params,
  });
}


/**
 * Add a confirmed fitment (Fitment tab manual YMM). Variables:
 * `{ sellerId, partId, payload }`. POSTs the YMM. No invalidation here — the
 * caller's `onSuccess` refetches the detail aggregate + invalidates the list.
 */
function usePartFitmentAddMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId, partId, payload }) => apiClient.request({
      url: `/inventory/parts/${sellerId}/${partId}/fitments/`,
      method: 'POST',
      payload,
    }),
    ...params,
  });
}


/**
 * Delete a confirmed fitment (Fitment tab). Variables:
 * `{ sellerId, partId, fitmentId }`. No invalidation here — the caller's
 * `onSuccess` refetches the detail aggregate + invalidates the list.
 */
function usePartFitmentDeleteMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId, partId, fitmentId }) => apiClient.request({
      url: `/inventory/parts/${sellerId}/${partId}/fitments/${fitmentId}/`,
      method: 'DELETE',
    }),
    ...params,
  });
}


/**
 * Accept / reject interchange suggestions (M5 #95). Variables:
 * `{ sellerId, partId, decision, items, suggestionsKey }`. POSTs the decision
 * then invalidates BOTH the parts list (the confirmed-fitment column is
 * list-visible) AND the per-part suggestions query so the authoritative refetch
 * reconciles the optimistic accept/reject state — preserved exactly from the
 * original inline `decideMutation` (which invalidated
 * `['root','','inventory','parts',sellerId]` + `suggestionsQuery.queryKey`). The
 * caller still drives the optimistic add/revert in component state and refetches
 * the detail aggregate via `onSuccess`.
 */
function usePartFitmentDecideMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, partId, decision, items, suggestionsKey }) => apiClient
      .request({
        url: `/inventory/parts/${sellerId}/${partId}/fitments/decide/`,
        method: 'POST',
        payload: { decision, items },
      })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: partsKeyPrefix(sellerId) });
        queryClient.invalidateQueries({ queryKey: suggestionsKey });
      }),
    ...params,
  });
}


/**
 * Toggle a listing channel on/off (Listings tab). Variables:
 * `{ sellerId, partId, channel, live, connection_id? }`. POSTs the channel
 * state; when `connection_id` is supplied (a connected eBay account, Plan 3
 * Slice E) the backend records a connection-scoped `ChannelListing` and the
 * `channel` is implied server-side. Manual channels omit `connection_id` and
 * behave exactly as before. No invalidation here — the caller's `onSuccess`
 * refetches the detail aggregate + invalidates the list (the table channel dots
 * are list-visible).
 */
function usePartChannelToggleMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId, partId, channel, live, connection_id: connectionId }) => apiClient
      .request({
        url: `/inventory/parts/${sellerId}/${partId}/channels/`,
        method: 'POST',
        payload: { channel, live, ...(connectionId ? { connection_id: connectionId } : {}) },
      }),
    ...params,
  });
}


/**
 * Publish a part to eBay (Listings tab, M17 Slice). Variables:
 * `{ sellerId, partId, connectionId }`. POSTs to the publish endpoint; when
 * `connectionId` is supplied the backend publishes to that connected eBay
 * account, otherwise it publishes to the org's default. Returns the created
 * `ChannelListing`. No invalidation here — the caller's `onSuccess` (`onMutate`,
 * i.e. `refetchDetail`) refetches the detail aggregate + invalidates the parts
 * list so the channel dots + row state stay in sync, matching
 * `usePartChannelToggleMutation`.
 */
function usePartPublishEbayMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId, partId, connectionId }) => apiClient.request({
      url: `/inventory/parts/${sellerId}/${partId}/publish-ebay/`,
      method: 'POST',
      payload: connectionId ? { connection_id: connectionId } : {},
    }),
    ...params,
  });
}


/**
 * Delete a part from the drawer. Variables: `{ sellerId, partId, keyPrefix }`.
 * DELETEs then invalidates the parts list (`keyPrefix`). The caller's
 * `onSuccess` closes the drawer.
 */
function usePartDeleteMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, partId, keyPrefix }) => apiClient
      .request({ url: `/inventory/parts/${sellerId}/${partId}/`, method: 'DELETE' })
      .then(() => queryClient.invalidateQueries({ queryKey: keyPrefix })),
    ...params,
  });
}


/**
 * Duplicate a part (drawer overflow menu). Variables: `{ sellerId, payload,
 * keyPrefix }` — the caller builds the `(copy)` payload from the source part.
 * POSTs then invalidates the parts list (`keyPrefix`).
 */
function usePartDuplicateMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, payload, keyPrefix }) => apiClient
      .request({ url: `/inventory/parts/${sellerId}/`, method: 'POST', payload })
      .then(() => queryClient.invalidateQueries({ queryKey: keyPrefix })),
    ...params,
  });
}


/**
 * Revert a sold part to available (#132). Variables: `{ sellerId, saleId,
 * status, keyPrefix }` where `status` is 'returned' | 'voided'. POSTs the sale
 * revert (the sale is returned/voided, never deleted, so M8 reporting stays
 * honest) then invalidates the parts list (`keyPrefix`).
 */
function usePartSaleRevertMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, saleId, status, keyPrefix }) => apiClient
      .request({ url: `/sales/${sellerId}/${saleId}/revert/`, method: 'POST', payload: { status } })
      .then(() => queryClient.invalidateQueries({ queryKey: keyPrefix })),
    ...params,
  });
}


export {
  usePartChannelToggleMutation,
  usePartDeleteMutation,
  usePartDuplicateMutation,
  usePartFitmentAddMutation,
  usePartFitmentDecideMutation,
  usePartFitmentDeleteMutation,
  usePartListInvalidateMutation,
  usePartPhotoUploadMutation,
  usePartPublishEbayMutation,
  usePartSaleRevertMutation,
};
